// Proposition TRAME — lectures de l'API (React Query). Contrat : docs/CONTRAT-API-PROPOSITION.md.
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.ts';
import type { PlanVue } from '../modele.ts';
import type { CalculVue, ComparaisonVue, DetailVue, ElementVue, PieceVue } from './logique.ts';

export const cles = {
  plans: (d: string) => ['conception-plans', d],
  pieces: (p: string) => ['proposition-pieces', p],
  elements: (p: string) => ['proposition-elements', p],
  calculs: (d: string) => ['proposition-calculs', d],
  detail: (d: string, id: string) => ['proposition-detail', d, id],
  comparaison: (d: string, run: string) => ['proposition-comparaison', d, run],
};

export function usePlans(dossierId: string) {
  return useQuery({ queryKey: cles.plans(dossierId), queryFn: () => api.get<{ plans: PlanVue[] }>(`/api/dossiers/${dossierId}/plans`) });
}
/** Pièces et éléments de TOUS les plans du dossier (la liste des manques et le visuel les croisent). */
export function usePiecesElements(plans: PlanVue[]) {
  const pieces = useQueries({ queries: plans.map((p) => ({ queryKey: cles.pieces(p.id), queryFn: () => api.get<{ echelleValidee: boolean; pieces: PieceVue[] }>(`/api/plans/${p.id}/pieces`) })) });
  const elements = useQueries({ queries: plans.map((p) => ({ queryKey: cles.elements(p.id), queryFn: () => api.get<{ elements: ElementVue[] }>(`/api/plans/${p.id}/elements`) })) });
  return {
    pieces: pieces.flatMap((q) => q.data?.pieces ?? []),
    elements: elements.flatMap((q) => q.data?.elements ?? []),
    echelles: new Map(plans.map((p, i) => [p.id, pieces[i]?.data?.echelleValidee ?? p.echelle.statut === 'validee'])),
    chargement: pieces.some((q) => q.isLoading) || elements.some((q) => q.isLoading),
    erreur: pieces.find((q) => q.isError)?.error ?? elements.find((q) => q.isError)?.error ?? null,
  };
}
export function useCalculs(dossierId: string) {
  return useQuery({ queryKey: cles.calculs(dossierId), queryFn: () => api.get<{ calculs: CalculVue[] }>(`/api/dossiers/${dossierId}/proposition`) });
}
export function useDetail(dossierId: string, id: string | null) {
  return useQuery({ queryKey: cles.detail(dossierId, id ?? ''), enabled: !!id, staleTime: 60_000,
    queryFn: () => api.get<DetailVue>(`/api/dossiers/${dossierId}/proposition/${id}`) });
}
export function useDetails(dossierId: string, ids: string[]) {
  return useQueries({ queries: ids.map((id) => ({ queryKey: cles.detail(dossierId, id), staleTime: 60_000, queryFn: () => api.get<DetailVue>(`/api/dossiers/${dossierId}/proposition/${id}`) })) });
}
export function useComparaison(dossierId: string, run: string | null) {
  return useQuery({ queryKey: cles.comparaison(dossierId, run ?? ''), enabled: !!run,
    queryFn: () => api.get<ComparaisonVue>(`/api/dossiers/${dossierId}/proposition/comparaison?run=${run}`) });
}
export function useRafraichir(dossierId: string) {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: ['proposition-pieces'] }), qc.invalidateQueries({ queryKey: ['proposition-elements'] }),
    qc.invalidateQueries({ queryKey: cles.calculs(dossierId) }), qc.invalidateQueries({ queryKey: ['proposition-detail', dossierId] }),
    qc.invalidateQueries({ queryKey: ['proposition-comparaison', dossierId] }),
  ]);
}
